import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

const SMTPSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    secure: false,
    auth: {
      user: '',
      pass: ''
    },
    from: {
      name: '',
      email: ''
    },
    toEmail: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiService.getSMTPSettings();
      if (response.success && response.data) {
        setFormData({
          host: response.data.host || '',
          port: response.data.port || 587,
          secure: response.data.secure !== undefined ? response.data.secure : false,
          auth: {
            user: response.data.auth?.user || '',
            pass: response.data.auth?.pass || ''
          },
          from: {
            name: response.data.from?.name || '',
            email: response.data.from?.email || ''
          },
          toEmail: response.data.toEmail || ''
        });
      }
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      setMessage({ type: 'error', content: 'Failed to load SMTP settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', content: '' });

    try {
      const response = await apiService.updateSMTPSettings(formData);
      if (response.success) {
        setMessage({ type: 'success', content: 'SMTP settings saved successfully' });
      } else {
        setMessage({ type: 'error', content: response.message || 'Failed to save SMTP settings' });
      }
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      setMessage({ type: 'error', content: error.message || 'Failed to save SMTP settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async (emailType = 'admin') => {
    setMessage({ type: '', content: '' });
    
    try {
      let testEmail;
      let successMessage;
      
      if (emailType === 'admin') {
        testEmail = user.email || formData.from.email;
        successMessage = 'Test email sent successfully to your admin email! Please check your inbox.';
      } else {
        testEmail = formData.toEmail;
        successMessage = 'Test email sent successfully to the "To Email" address! Please check the inbox.';
      }

      if (!testEmail) {
        setMessage({ type: 'error', content: 'Email address not available for testing.' });
        return;
      }

      const response = await apiService.testSMTPSettings(testEmail);
      if (response.success) {
        setMessage({ type: 'success', content: successMessage });
      } else {
        setMessage({ type: 'error', content: response.message || 'Failed to send test email' });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setMessage({ type: 'error', content: error.message || 'Failed to send test email' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading SMTP settings...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-border_gray">
        <div className="p-6 border-b border-border_gray">
          <h2 className="text-2xl font-sans font-bold text-slate">SMTP Settings</h2>
          <p className="mt-2 text-sm text-gray-600">
            Configure SMTP settings for sending emails from the application.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {message.content && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center">
                <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} mr-2`}></i>
                {message.content}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host
              </label>
              <input
                type="text"
                name="host"
                value={formData.host}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="smtp.gmail.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port
              </label>
              <input
                type="number"
                name="port"
                value={formData.port}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="587"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="secure"
                  checked={formData.secure}
                  onChange={handleChange}
                  className="rounded text-crimson focus:ring-crimson"
                />
                <span className="text-sm font-medium text-gray-700">
                  Use secure connection (TLS/SSL)
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Username
              </label>
              <input
                type="text"
                name="auth.user"
                value={formData.auth.user}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="your-email@gmail.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="auth.pass"
                  value={formData.auth.pass}
                  onChange={handleChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 px-3 py-2 text-gray-500 hover:text-gray-700"
                >
                  <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Name
              </label>
              <input
                type="text"
                name="from.name"
                value={formData.from.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="Forte Engineering"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email
              </label>
              <input
                type="email"
                name="from.email"
                value={formData.from.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="noreply@forteengineering.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Email (Form Submissions)
              </label>
              <input
                type="email"
                name="toEmail"
                value={formData.toEmail}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
                placeholder="admin@forteengineering.com"
                required
              />
              <p className="mt-1 text-sm text-gray-600">
                All form submissions from the website will be sent to this email address.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => handleTestEmail('admin')}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors duration-200"
                disabled={saving}
              >
                <i className="fas fa-paper-plane mr-2"></i>
                Test to Admin
              </button>
              
              <button
                type="button"
                onClick={() => handleTestEmail('toEmail')}
                className="px-4 py-2 text-sm font-medium text-purple-600 bg-white border border-purple-600 rounded-lg hover:bg-purple-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-600 transition-colors duration-200"
                disabled={saving || !formData.toEmail}
              >
                <i className="fas fa-envelope mr-2"></i>
                Test to Form Email
              </button>
            </div>
            
            <button
              type="submit"
              className="px-6 py-2 text-sm font-medium text-white bg-crimson rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-crimson transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <i className="fas fa-info-circle text-blue-600 mt-0.5 mr-3"></i>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">SMTP Configuration Tips:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>For Gmail: Use smtp.gmail.com, port 587, and enable "Less secure app access" or use App Password</li>
              <li>For Outlook: Use smtp-mail.outlook.com, port 587</li>
              <li>For Yahoo: Use smtp.mail.yahoo.com, port 587 or 465</li>
              <li>Most providers require app-specific passwords for SMTP authentication</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMTPSettings;